// What goes out next, grouped by the day it goes out on.
//
// It stays in the right-hand column on a desktop, beside the composer rather
// than below it, because the question it answers — "have I already got
// something going out then?" — is asked *while* writing, not after.
//
// The rows are our own scheduled posts. Nothing here was read back from a
// network: a scheduled post has not been sent yet, so there is nothing a
// network could have told us about it.

import { Link } from "react-router-dom";
import { clock, dayHeading } from "./format";
import { useZone } from "../lib/userSettings";
import type { Dashboard, DashboardUpcoming } from "./types";

/** Soonest first, split into days, keeping the order the server sent. */
function byDay(rows: DashboardUpcoming[], zone: string): Array<[string, DashboardUpcoming[]]> {
  const days = new Map<string, DashboardUpcoming[]>();
  for (const row of rows) {
    const key = dayHeading(row.scheduled_at, zone);
    const list = days.get(key);
    if (list) list.push(row);
    else days.set(key, [row]);
  }
  return [...days.entries()];
}

export function UpcomingScheduleCard({ data }: { data: Dashboard }) {
  const zone = useZone();
  const groups = byDay(data.upcoming, zone);

  return (
    <section className="card dash-card upcoming-card" aria-labelledby="upcoming-head">
      <div className="card-head">
        <h2 id="upcoming-head">Upcoming schedule</h2>
        <Link to="/calendar" className="card-head-link">View calendar →</Link>
      </div>

      {groups.length === 0 ? (
        <p className="muted empty-state">
          No scheduled posts yet. <Link to="/compose">Create your first scheduled post</Link> and
          it appears here.
        </p>
      ) : (
        <>
          {groups.map(([day, rows]) => (
            <div key={day} className="upcoming-day">
              <h3>
                {day}
                <span className="chip">{rows.length} post{rows.length === 1 ? "" : "s"}</span>
              </h3>
              <ul>
                {rows.map((p) => (
                  <li key={p.id}>
                    <time dateTime={p.scheduled_at}>{clock(p.scheduled_at, zone)}</time>
                    <Link to={`/posts/${p.id}`} className="upcoming-what">
                      {p.title || `Untitled ${p.kind}`}
                    </Link>
                    <span className="muted upcoming-where">
                      {p.destinations} destination{p.destinations === 1 ? "" : "s"}
                    </span>
                    <span className="chip">Scheduled</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {data.totals.scheduled_ahead > data.upcoming.length && (
            <p className="muted upcoming-rest">
              {data.totals.scheduled_ahead - data.upcoming.length} more scheduled after these —{" "}
              <Link to="/calendar">see the calendar</Link>.
            </p>
          )}
        </>
      )}
    </section>
  );
}
